import { register, login } from '../services/auth';
import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { getProfile } from '../services/auth';
import api from '../services/api';
import { syncTourStatus } from '../services/tourService';
import { setAuthenticatedUser, clearAuthenticatedUser, trackEvent } from '../services/applicationInsights';
import { secureGet, secureSet, secureRemove } from '../services/secureStorage';
import { isNativePlatform } from '../services/platform';
import { isBiometricEnrolled } from '../services/biometricService';

export const AuthContext = createContext();

// Inactivity timeout settings (in milliseconds)
const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes
const WARNING_BEFORE_TIMEOUT = 2 * 60 * 1000; // Show warning 2 minutes before logout
const LAST_ACTIVITY_KEY = 'lastActivityTimestamp';

// Custom hook for using the auth context
export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  // Refs for timers
  const warningTimerRef = useRef(null);
  const logoutTimerRef = useRef(null);
  const tokenLoadedRef = useRef(false);

  // Load token from secure storage on mount
  useEffect(() => {
    const loadToken = async () => {
      const storedToken = await secureGet('token');
      if (storedToken) {
        setToken(storedToken);
      } else {
        // No session token — check if biometric is enrolled for unlock
        if (isNativePlatform()) {
          const enrolled = await isBiometricEnrolled();
          if (enrolled) {
            setIsLocked(true);
          }
        }
        setLoading(false);
      }
      tokenLoadedRef.current = true;
    };
    loadToken();
  }, []);

  useEffect(() => {
    // Don't run until initial token load completes
    if (!tokenLoadedRef.current) return;

    const loadUser = async () => {
      if (token) {
        try {
          const userData = await getProfile(token);
          setUser(userData);
          setIsAuthenticated(true);
          // Track user in Application Insights
          setAuthenticatedUser(userData.id);
          // Sync tour status from backend on login
          await syncTourStatus(token);
        } catch (err) {
          await secureRemove('token');
          setToken(null);
          setIsAuthenticated(false);
          setError('Authentication failed. Please login again.');
          // Clear Application Insights user context
          clearAuthenticatedUser();
        }
      }
      setLoading(false);
    };

    loadUser();
  }, [token]);

  // Reset inactivity timers
  const resetInactivityTimers = useCallback(() => {
    // Clear existing timers
    if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
    if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);

    // Hide warning if it was showing
    setShowInactivityWarning(false);

    // Only set timers if authenticated
    if (isAuthenticated) {
      // Store the current timestamp in localStorage
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());

      // Set warning timer (58 minutes)
      warningTimerRef.current = setTimeout(() => {
        setShowInactivityWarning(true);
      }, INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT);

      // Set logout timer (60 minutes)
      logoutTimerRef.current = setTimeout(async () => {
        setShowInactivityWarning(false);
        // On native with biometric enrolled, lock instead of full logout
        if (isNativePlatform()) {
          const enrolled = await isBiometricEnrolled();
          if (enrolled) {
            lockApp();
            return;
          }
        }
        logoutUser();
      }, INACTIVITY_TIMEOUT);
    }
  }, [isAuthenticated]);

  // Check if session has expired based on stored timestamp
  const checkSessionExpiry = useCallback(() => {
    const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
    if (!lastActivityStr) return false;

    const lastActivity = parseInt(lastActivityStr, 10);
    const elapsed = Date.now() - lastActivity;

    if (elapsed >= INACTIVITY_TIMEOUT) {
      // Session expired - log out
      console.log('Session expired due to inactivity (elapsed:', Math.round(elapsed / 60000), 'minutes)');
      localStorage.removeItem(LAST_ACTIVITY_KEY);
      logoutUser();
      return true;
    } else if (elapsed >= INACTIVITY_TIMEOUT - WARNING_BEFORE_TIMEOUT) {
      // Within warning window - show warning
      setShowInactivityWarning(true);
      // Set a timer for the remaining time until logout
      const remainingTime = INACTIVITY_TIMEOUT - elapsed;
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
      logoutTimerRef.current = setTimeout(() => {
        setShowInactivityWarning(false);
        logoutUser();
      }, remainingTime);
      return false;
    }
    return false;
  }, []);

  // Track user activity and reset timers
  useEffect(() => {
    if (!isAuthenticated) return;

    // Check session expiry on mount (handles page reload, returning to tab after sleep, etc.)
    if (checkSessionExpiry()) return;

    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    // Throttle the reset to avoid excessive timer resets
    let lastActivity = Date.now();
    const throttleMs = 30000; // Only reset timers once per 30 seconds of activity

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastActivity > throttleMs) {
        lastActivity = now;
        resetInactivityTimers();
      }
    };

    // Handle visibility change (user returns to tab after being away)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if session expired while tab was hidden/computer was sleeping
        if (!checkSessionExpiry()) {
          // Session still valid - reset timers
          resetInactivityTimers();
        }
      }
    };

    // Add event listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, handleActivity, { passive: true });
    });
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Initialize timers (also stores initial timestamp)
    resetInactivityTimers();

    // Cleanup
    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, handleActivity);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (warningTimerRef.current) clearTimeout(warningTimerRef.current);
      if (logoutTimerRef.current) clearTimeout(logoutTimerRef.current);
    };
  }, [isAuthenticated, resetInactivityTimers, checkSessionExpiry]);

  // Handle "Stay Logged In" from warning modal
  const handleStayLoggedIn = useCallback(() => {
    setShowInactivityWarning(false);
    resetInactivityTimers();
  }, [resetInactivityTimers]);

  // Register user
  const registerUser = async (formData) => {
    try {
      const { token, user } = await register(formData);
      await secureSet('token', token);
      // Reset activity timestamp so session timer starts fresh from registration
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      setError(null);
      // Track user in Application Insights
      setAuthenticatedUser(user.id);
      trackEvent('UserRegistered', { userId: user.id });
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Registration failed. Please try again.');
      return false;
    }
  };

  // Login user
  const loginUser = async (formData) => {
    try {
      const { token, user } = await login(formData);
      await secureSet('token', token);
      // Reset activity timestamp so session timer starts fresh from login
      localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
      setToken(token);
      setUser(user);
      setIsAuthenticated(true);
      setError(null);
      // Track user in Application Insights
      setAuthenticatedUser(user.id);
      trackEvent('UserLoggedIn', { userId: user.id });
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Login failed. Please check your credentials.');
      return false;
    }
  };

  // Logout user
  const logoutUser = async () => {
    // Track logout in Application Insights before clearing user
    trackEvent('UserLoggedOut', { userId: user?.id });
    clearAuthenticatedUser();

    // If demo user, call demo-logout endpoint to clear demo data
    if (user?.isDemo && token) {
      try {
        await api.post('/api/auth/demo-logout');
      } catch (err) {
        // Ignore errors during demo logout - still proceed with local logout
        console.error('Demo logout error:', err);
      }
    }

    await secureRemove('token');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setError(null);
  };

  // Lock app (biometric unlock required)
  const lockApp = useCallback(async () => {
    setIsLocked(true);
    setToken(null);
    setUser(null);
    setIsAuthenticated(false);
    await secureRemove('token');
    // Don't clear biometric token - that stays for unlock
  }, []);

  // Unlock with biometric (receives fresh JWT)
  const unlockWithBiometric = useCallback(async (newToken, userData) => {
    await secureSet('token', newToken);
    localStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString());
    setToken(newToken);
    setUser(userData);
    setIsAuthenticated(true);
    setIsLocked(false);
    setAuthenticatedUser(userData.id);
  }, []);

  // Listen for app state changes (native only) to trigger biometric lock
  useEffect(() => {
    if (!isNativePlatform()) return;

    let appListener = null;

    const setupListener = async () => {
      try {
        const { App } = await import('@capacitor/app');
        appListener = await App.addListener('appStateChange', async ({ isActive }) => {
          if (isActive && !isLocked) {
            // App came to foreground - check if session expired
            const lastActivityStr = localStorage.getItem(LAST_ACTIVITY_KEY);
            if (lastActivityStr) {
              const elapsed = Date.now() - parseInt(lastActivityStr, 10);
              if (elapsed >= INACTIVITY_TIMEOUT) {
                // Session expired - check if biometric is enrolled
                const enrolled = await isBiometricEnrolled();
                if (enrolled) {
                  lockApp();
                }
              }
            }
          }
        });
      } catch (err) {
        console.error('Failed to setup app state listener:', err);
      }
    };

    setupListener();

    return () => {
      if (appListener) appListener.remove();
    };
  }, [isLocked, lockApp]);

  // Clear error
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        isAuthenticated,
        loading,
        error,
        isLocked,
        registerUser,
        loginUser,
        logoutUser,
        lockApp,
        unlockWithBiometric,
        clearError,
        setToken,
        setUser,
        setIsAuthenticated
      }}
    >
      {children}

      {/* Inactivity Warning Modal */}
      {showInactivityWarning && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-slate-900 rounded-lg p-6 max-w-sm mx-4 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Session Timeout Warning
            </h3>
            <p className="text-slate-600 dark:text-slate-400 mb-4">
              You will be logged out in 2 minutes due to inactivity.
            </p>
            <div className="flex gap-3">
              <button
                onClick={handleStayLoggedIn}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Stay Logged In
              </button>
              <button
                onClick={logoutUser}
                className="flex-1 px-4 py-2 bg-slate-200 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-300 transition-colors"
              >
                Log Out Now
              </button>
            </div>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
};
